import { useRouter } from 'next/router';
import React__default, { useEffect, useContext, useRef, useCallback, createElement, createContext, memo, useMemo } from 'react';

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _createForOfIteratorHelperLoose(o, allowArrayLike) {
  var it = typeof Symbol !== "undefined" && o[Symbol.iterator] || o["@@iterator"];
  if (it) return (it = it.call(o)).next.bind(it);

  if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") {
    if (it) o = it;
    var i = 0;
    return function () {
      if (i >= o.length) return {
        done: true
      };
      return {
        done: false,
        value: o[i++]
      };
    };
  }

  throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var __assign = undefined && undefined.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

function withDefault(param, defaultValue, includeNull) {
  if (includeNull === void 0) {
    includeNull = true;
  }

  var decodeWithDefault = function decodeWithDefault() {
    var args = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      args[_i] = arguments[_i];
    }

    var decodedValue = param.decode.apply(param, args);

    if (decodedValue === undefined) {
      return defaultValue;
    }

    if (includeNull) {
      if (decodedValue === null) {
        return defaultValue;
      } else {
        return decodedValue;
      }
    }

    return decodedValue;
  };

  return __assign(__assign({}, param), {
    decode: decodeWithDefault
  });
}

var __spreadArray = undefined && undefined.__spreadArray || function (to, from) {
  for (var i = 0, il = from.length, j = to.length; i < il; i++, j++) {
    to[j] = from[i];
  }

  return to;
};
/**
 * Interprets an encoded string and returns either the string or null/undefined if not available.
 * Ignores array inputs (takes just first element in array)
 * @param input encoded string
 */


function getEncodedValue(input, allowEmptyString) {
  if (input == null) {
    return input;
  } // '' or []


  if (input.length === 0 && (!allowEmptyString || allowEmptyString && input !== '')) {
    return null;
  }

  var str = input instanceof Array ? input[0] : input;

  if (str == null) {
    return str;
  }

  if (!allowEmptyString && str === '') {
    return null;
  }

  return str;
}
/**
 * Interprets an encoded string and return null/undefined or an array with
 * the encoded string contents
 * @param input encoded string
 */


function getEncodedValueArray(input) {
  if (input == null) {
    return input;
  }

  return input instanceof Array ? input : input === '' ? [] : [input];
}
/**
 * Encodes a date as a string in YYYY-MM-DD format.
 *
 * @param {Date} date
 * @return {String} the encoded date
 */


function encodeDate(date) {
  if (date == null) {
    return date;
  }

  var year = date.getFullYear();
  var month = date.getMonth() + 1;
  var day = date.getDate();
  return year + "-" + (month < 10 ? "0" + month : month) + "-" + (day < 10 ? "0" + day : day);
}
/**
 * Converts a date in the format 'YYYY-mm-dd...' into a proper date, because
 * new Date() does not do that correctly. The date can be as complete or incomplete
 * as necessary (aka, '2015', '2015-10', '2015-10-01').
 * It will not work for dates that have times included in them.
 *
 * If an array is provided, only the first entry is used.
 *
 * @param  {String} input String date form like '2015-10-01'
 * @return {Date} parsed date
 */

function decodeDate(input) {
  var dateString = getEncodedValue(input);
  if (dateString == null) return dateString;
  var parts = dateString.split('-'); // may only be a year so won't even have a month

  if (parts[1] != null) {
    parts[1] -= 1; // Note: months are 0-based
  } else {
    // just a year, set the month and day to the first
    parts[1] = 0;
    parts[2] = 1;
  }

  var decoded = new (Date.bind.apply(Date, __spreadArray([void 0], parts)))();

  if (isNaN(decoded.getTime())) {
    return null;
  }

  return decoded;
}
/**
 * Encodes a date as a string in ISO 8601 ("2019-05-28T10:58:40Z") format.
 *
 * @param {Date} date
 * @return {String} the encoded date
 */

function encodeDateTime(date) {
  if (date == null) {
    return date;
  }

  return date.toISOString();
}
/**
 * Converts a date in the https://en.wikipedia.org/wiki/ISO_8601 format.
 * For allowed inputs see specs:
 *  - https://tools.ietf.org/html/rfc2822#page-14
 *  - http://www.ecma-international.org/ecma-262/5.1/#sec-15.9.1.15
 *
 * If an array is provided, only the first entry is used.
 *
 * @param  {String} input String date form like '1995-12-17T03:24:00'
 * @return {Date} parsed date
 */

function decodeDateTime(input) {
  var dateString = getEncodedValue(input);
  if (dateString == null) return dateString;
  var decoded = new Date(dateString);

  if (isNaN(decoded.getTime())) {
    return null;
  }

  return decoded;
}
/**
 * Encodes a boolean as a string. true -> "1", false -> "0".
 *
 * @param {Boolean} bool
 * @return {String} the encoded boolean
 */

function encodeBoolean(bool) {
  if (bool == null) {
    return bool;
  }

  return bool ? '1' : '0';
}
/**
 * Decodes a boolean from a string. "1" -> true, "0" -> false.
 * Everything else maps to undefined.
 *
 * If an array is provided, only the first entry is used.
 *
 * @param {String} input the encoded boolean string
 * @return {Boolean} the boolean value
 */

function decodeBoolean(input) {
  var boolStr = getEncodedValue(input);
  if (boolStr == null) return boolStr;

  if (boolStr === '1') {
    return true;
  } else if (boolStr === '0') {
    return false;
  }

  return null;
}
/**
 * Encodes a number as a string.
 *
 * @param {Number} num
 * @return {String} the encoded number
 */

function encodeNumber(num) {
  if (num == null) {
    return num;
  }

  return String(num);
}
/**
 * Decodes a number from a string. If the number is invalid,
 * it returns undefined.
 *
 * If an array is provided, only the first entry is used.
 *
 * @param {String} input the encoded number string
 * @return {Number} the number value
 */

function decodeNumber(input) {
  var numStr = getEncodedValue(input);
  if (numStr == null) return numStr;
  if (numStr === '') return null;
  var result = +numStr;
  return result;
}
/**
 * Encodes a string while safely handling null and undefined values.
 *
 * @param {String} str a string to encode
 * @return {String} the encoded string
 */

function encodeString(str) {
  if (str == null) {
    return str;
  }

  return String(str);
}
/**
 * Decodes a string while safely handling null and undefined values.
 *
 * If an array is provided, only the first entry is used.
 *
 * @param {String} input the encoded string
 * @return {String} the string value
 */

function decodeString(input) {
  var str = getEncodedValue(input, true);
  if (str == null) return str;
  return String(str);
}
/**
 * Decodes an enum value while safely handling null and undefined values.
 *
 * If an array is provided, only the first entry is used.
 *
 * @param {String} input the encoded string
 * @param {String[]} enumValues allowed enum values
 * @return {String} the string value from enumValues
 */

function decodeEnum(input, enumValues) {
  var str = decodeString(input);
  if (str == null) return str;
  return enumValues.includes(str) ? str : undefined;
}
/**
 * Encodes anything as a JSON string.
 *
 * @param {Any} any The thing to be encoded
 * @return {String} The JSON string representation of any
 */

function encodeJson(any) {
  if (any == null) {
    return any;
  }

  return JSON.stringify(any);
}
/**
 * Decodes a JSON string into javascript
 *
 * If an array is provided, only the first entry is used.
 *
 * @param {String} input The JSON string representation
 * @return {Any} The javascript representation
 */

function decodeJson(input) {
  var jsonStr = getEncodedValue(input);
  if (jsonStr == null) return jsonStr;
  var result = null;

  try {
    result = JSON.parse(jsonStr);
  } catch (e) {
    /* ignore errors, returning undefined */
  }

  return result;
}
/**
 * Encodes an array as a JSON string.
 *
 * @param {Array} array The array to be encoded
 * @return {String[]} The array of strings to be put in the URL
 * as repeated query parameters
 */

function encodeArray(array) {
  if (array == null) {
    return array;
  }

  return array;
}
/**
 * Decodes an array or singular value and returns it as an array
 * or undefined if falsy. Filters out undefined values.
 *
 * @param {String | Array} input The input value
 * @return {Array} The javascript representation
 */

function decodeArray(input) {
  var arr = getEncodedValueArray(input);
  if (arr == null) return arr;
  return arr;
}
/**
 * Encodes a numeric array as a JSON string.
 *
 * @param {Array} array The array to be encoded
 * @return {String[]} The array of strings to be put in the URL
 * as repeated query parameters
 */

function encodeNumericArray(array) {
  if (array == null) {
    return array;
  }

  return array.map(String);
}
/**
 * Decodes an array or singular value and returns it as an array
 * or undefined if falsy. Filters out undefined and NaN values.
 *
 * @param {String | Array} input The input value
 * @return {Array} The javascript representation
 */

function decodeNumericArray(input) {
  var arr = decodeArray(input);
  if (arr == null) return arr;
  return arr.map(function (d) {
    return d === '' || d == null ? null : +d;
  });
}
/**
 * Encodes an array as a delimited string. For example,
 * ['a', 'b'] -> 'a_b' with entrySeparator='_'
 *
 * @param array The array to be encoded
 * @param entrySeparator The string used to delimit entries
 * @return The array as a string with elements joined by the
 * entry separator
 */

function encodeDelimitedArray(array, entrySeparator) {
  if (entrySeparator === void 0) {
    entrySeparator = '_';
  }

  if (array == null) {
    return array;
  }

  return array.join(entrySeparator);
}
/**
 * Decodes a delimited string into javascript array. For example,
 * 'a_b' -> ['a', 'b'] with entrySeparator='_'
 *
 * If an array is provided as input, only the first entry is used.
 *
 * @param {String} input The JSON string representation
 * @param entrySeparator The array as a string with elements joined by the
 * entry separator
 * @return {Array} The javascript representation
 */

function decodeDelimitedArray(input, entrySeparator) {
  if (entrySeparator === void 0) {
    entrySeparator = '_';
  }

  var arrayStr = getEncodedValue(input, true);
  if (arrayStr == null) return arrayStr;
  if (arrayStr === '') return [];
  return arrayStr.split(entrySeparator);
}
/**
 * Encodes a numeric array as a delimited string. (alias of encodeDelimitedArray)
 * For example, [1, 2] -> '1_2' with entrySeparator='_'
 *
 * @param {Array} array The array to be encoded
 * @return {String} The JSON string representation of array
 */

var encodeDelimitedNumericArray = encodeDelimitedArray;
/**
 * Decodes a delimited string into javascript array where all entries are numbers
 * For example, '1_2' -> [1, 2] with entrySeparator='_'
 *
 * If an array is provided as input, only the first entry is used.
 *
 * @param {String} jsonStr The JSON string representation
 * @return {Array} The javascript representation
 */

function decodeDelimitedNumericArray(arrayStr, entrySeparator) {
  if (entrySeparator === void 0) {
    entrySeparator = '_';
  }

  var decoded = decodeDelimitedArray(arrayStr, entrySeparator);
  if (decoded == null) return decoded;
  return decoded.map(function (d) {
    return d === '' || d == null ? null : +d;
  });
}
/**
 * Encode simple objects as readable strings. Works only for simple,
 * flat objects where values are numbers, strings.
 *
 * For example { foo: bar, boo: baz } -> "foo-bar_boo-baz"
 *
 * @param {Object} object The object to encode
 * @param {String} keyValSeparator="-" The separator between keys and values
 * @param {String} entrySeparator="_" The separator between entries
 * @return {String} The encoded object
 */

function encodeObject(obj, keyValSeparator, entrySeparator) {
  if (keyValSeparator === void 0) {
    keyValSeparator = '-';
  }

  if (entrySeparator === void 0) {
    entrySeparator = '_';
  }

  if (obj == null) return obj; // null or undefined

  if (!Object.keys(obj).length) return ''; // {} case

  return Object.keys(obj).map(function (key) {
    return "" + key + keyValSeparator + obj[key];
  }).join(entrySeparator);
}
/**
 * Decodes a simple object to javascript. Currently works only for simple,
 * flat objects where values are strings.
 *
 * For example "foo-bar_boo-baz" -> { foo: bar, boo: baz }
 *
 * If an array is provided as input, only the first entry is used.
 *
 * @param {String} input The object string to decode
 * @param {String} keyValSeparator="-" The separator between keys and values
 * @param {String} entrySeparator="_" The separator between entries
 * @return {Object} The javascript object
 */

function decodeObject(input, keyValSeparator, entrySeparator) {
  if (keyValSeparator === void 0) {
    keyValSeparator = '-';
  }

  if (entrySeparator === void 0) {
    entrySeparator = '_';
  }

  var objStr = getEncodedValue(input, true);
  if (objStr == null) return objStr;
  if (objStr === '') return {};
  var obj = {};
  var keyValSeparatorRegExp = new RegExp(keyValSeparator + "(.*)");
  objStr.split(entrySeparator).forEach(function (entryStr) {
    var _a = entryStr.split(keyValSeparatorRegExp),
        key = _a[0],
        value = _a[1];

    obj[key] = value;
  });
  return obj;
}
/**
 * Encode simple objects as readable strings. Alias of encodeObject.
 *
 * For example { foo: 123, boo: 521 } -> "foo-123_boo-521"
 *
 * @param {Object} object The object to encode
 * @param {String} keyValSeparator="-" The separator between keys and values
 * @param {String} entrySeparator="_" The separator between entries
 * @return {String} The encoded object
 */

var encodeNumericObject = encodeObject;
/**
 * Decodes a simple object to javascript where all values are numbers.
 * Currently works only for simple, flat objects.
 *
 * For example "foo-123_boo-521" -> { foo: 123, boo: 521 }
 *
 * If an array is provided as input, only the first entry is used.
 *
 * @param {String} input The object string to decode
 * @param {String} keyValSeparator="-" The separator between keys and values
 * @param {String} entrySeparator="_" The separator between entries
 * @return {Object} The javascript object
 */

function decodeNumericObject(input, keyValSeparator, entrySeparator) {
  if (keyValSeparator === void 0) {
    keyValSeparator = '-';
  }

  if (entrySeparator === void 0) {
    entrySeparator = '_';
  }

  var decoded = decodeObject(input, keyValSeparator, entrySeparator);
  if (decoded == null) return decoded; // convert to numbers

  var decodedNumberObj = {};

  for (var _i = 0, _a = Object.keys(decoded); _i < _a.length; _i++) {
    var key = _a[_i];
    decodedNumberObj[key] = decodeNumber(decoded[key]);
  }

  return decodedNumberObj;
}

/**
 * String values
 */

var StringParam = {
  encode: encodeString,
  decode: decodeString
};
/**
 * String enum
 */

var createEnumParam = function createEnumParam(enumValues) {
  return {
    encode: encodeString,
    decode: function decode(input) {
      return decodeEnum(input, enumValues);
    }
  };
};
/**
 * Numbers (integers or floats)
 */

var NumberParam = {
  encode: encodeNumber,
  decode: decodeNumber
};
/**
 * For flat objects where values are strings
 */

var ObjectParam = {
  encode: encodeObject,
  decode: decodeObject
};
/**
 * For flat arrays of strings, filters out undefined values during decode
 */

var ArrayParam = {
  encode: encodeArray,
  decode: decodeArray
};
/**
 * For flat arrays of strings, filters out undefined values during decode
 */

var NumericArrayParam = {
  encode: encodeNumericArray,
  decode: decodeNumericArray
};
/**
 * For any type of data, encoded via JSON.stringify
 */

var JsonParam = {
  encode: encodeJson,
  decode: decodeJson
};
/**
 * For simple dates (YYYY-MM-DD)
 */

var DateParam = {
  encode: encodeDate,
  decode: decodeDate,
  equals: function equals(valueA, valueB) {
    if (valueA === valueB) return true;
    if (valueA == null || valueB == null) return valueA === valueB; // ignore time of day

    return valueA.getFullYear() === valueB.getFullYear() && valueA.getMonth() === valueB.getMonth() && valueA.getDate() === valueB.getDate();
  }
};
/**
 * For dates in simplified extended ISO format (YYYY-MM-DDTHH:mm:ss.sssZ or ±YYYYYY-MM-DDTHH:mm:ss.sssZ)
 */

var DateTimeParam = {
  encode: encodeDateTime,
  decode: decodeDateTime,
  equals: function equals(valueA, valueB) {
    if (valueA === valueB) return true;
    if (valueA == null || valueB == null) return valueA === valueB;
    return valueA.valueOf() === valueB.valueOf();
  }
};
/**
 * For boolean values: 1 = true, 0 = false
 */

var BooleanParam = {
  encode: encodeBoolean,
  decode: decodeBoolean
};
/**
 * For flat objects where the values are numbers
 */

var NumericObjectParam = {
  encode: encodeNumericObject,
  decode: decodeNumericObject
};
/**
 * For flat arrays of strings, filters out undefined values during decode
 */

var DelimitedArrayParam = {
  encode: encodeDelimitedArray,
  decode: decodeDelimitedArray
};
/**
 * For flat arrays where the values are numbers, filters out undefined values during decode
 */

var DelimitedNumericArrayParam = {
  encode: encodeDelimitedNumericArray,
  decode: decodeDelimitedNumericArray
};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var strictUriEncode = function strictUriEncode(str) {
  return encodeURIComponent(str).replace(/[!'()*]/g, function (x) {
    return "%" + x.charCodeAt(0).toString(16).toUpperCase();
  });
};

var token = '%[a-f0-9]{2}';
var singleMatcher = /*#__PURE__*/new RegExp(token, 'gi');
var multiMatcher = /*#__PURE__*/new RegExp('(' + token + ')+', 'gi');

function decodeComponents(components, split) {
  try {
    // Try to decode the entire string first
    return decodeURIComponent(components.join(''));
  } catch (err) {// Do nothing
  }

  if (components.length === 1) {
    return components;
  }

  split = split || 1; // Split the array in 2 parts

  var left = components.slice(0, split);
  var right = components.slice(split);
  return Array.prototype.concat.call([], decodeComponents(left), decodeComponents(right));
}

function decode(input) {
  try {
    return decodeURIComponent(input);
  } catch (err) {
    var tokens = input.match(singleMatcher);

    for (var i = 1; i < tokens.length; i++) {
      input = decodeComponents(tokens, i).join('');
      tokens = input.match(singleMatcher);
    }

    return input;
  }
}

function customDecodeURIComponent(input) {
  // Keep track of all the replacements and prefill the map with the `BOM`
  var replaceMap = {
    '%FE%FF': "\uFFFD\uFFFD",
    '%FF%FE': "\uFFFD\uFFFD"
  };
  var match = multiMatcher.exec(input);

  while (match) {
    try {
      // Decode as big chunks as possible
      replaceMap[match[0]] = decodeURIComponent(match[0]);
    } catch (err) {
      var result = decode(match[0]);

      if (result !== match[0]) {
        replaceMap[match[0]] = result;
      }
    }

    match = multiMatcher.exec(input);
  } // Add `%C2` at the end of the map to make sure it does not replace the combinator before everything else


  replaceMap['%C2'] = "\uFFFD";
  var entries = Object.keys(replaceMap);

  for (var i = 0; i < entries.length; i++) {
    // Replace all decoded components
    var key = entries[i];
    input = input.replace(new RegExp(key, 'g'), replaceMap[key]);
  }

  return input;
}

var decodeUriComponent = function decodeUriComponent(encodedURI) {
  if (typeof encodedURI !== 'string') {
    throw new TypeError('Expected `encodedURI` to be of type `string`, got `' + typeof encodedURI + '`');
  }

  try {
    encodedURI = encodedURI.replace(/\+/g, ' '); // Try the built in decoder first

    return decodeURIComponent(encodedURI);
  } catch (err) {
    // Fallback to a more advanced decoder
    return customDecodeURIComponent(encodedURI);
  }
};

var splitOnFirst = function splitOnFirst(string, separator) {
  if (!(typeof string === 'string' && typeof separator === 'string')) {
    throw new TypeError('Expected the arguments to be of type `string`');
  }

  if (separator === '') {
    return [string];
  }

  var separatorIndex = string.indexOf(separator);

  if (separatorIndex === -1) {
    return [string];
  }

  return [string.slice(0, separatorIndex), string.slice(separatorIndex + separator.length)];
};

var filterObj = function filterObj(obj, predicate) {
  var ret = {};
  var keys = Object.keys(obj);
  var isArr = Array.isArray(predicate);

  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    var val = obj[key];

    if (isArr ? predicate.indexOf(key) !== -1 : predicate(key, val, obj)) {
      ret[key] = val;
    }
  }

  return ret;
};

var queryString = /*#__PURE__*/createCommonjsModule(function (module, exports) {

  var isNullOrUndefined = function isNullOrUndefined(value) {
    return value === null || value === undefined;
  };

  var encodeFragmentIdentifier = Symbol('encodeFragmentIdentifier');

  function encoderForArrayFormat(options) {
    switch (options.arrayFormat) {
      case 'index':
        return function (key) {
          return function (result, value) {
            var index = result.length;

            if (value === undefined || options.skipNull && value === null || options.skipEmptyString && value === '') {
              return result;
            }

            if (value === null) {
              return [].concat(result, [[encode(key, options), '[', index, ']'].join('')]);
            }

            return [].concat(result, [[encode(key, options), '[', encode(index, options), ']=', encode(value, options)].join('')]);
          };
        };

      case 'bracket':
        return function (key) {
          return function (result, value) {
            if (value === undefined || options.skipNull && value === null || options.skipEmptyString && value === '') {
              return result;
            }

            if (value === null) {
              return [].concat(result, [[encode(key, options), '[]'].join('')]);
            }

            return [].concat(result, [[encode(key, options), '[]=', encode(value, options)].join('')]);
          };
        };

      case 'comma':
      case 'separator':
      case 'bracket-separator':
        {
          var keyValueSep = options.arrayFormat === 'bracket-separator' ? '[]=' : '=';
          return function (key) {
            return function (result, value) {
              if (value === undefined || options.skipNull && value === null || options.skipEmptyString && value === '') {
                return result;
              } // Translate null to an empty string so that it doesn't serialize as 'null'


              value = value === null ? '' : value;

              if (result.length === 0) {
                return [[encode(key, options), keyValueSep, encode(value, options)].join('')];
              }

              return [[result, encode(value, options)].join(options.arrayFormatSeparator)];
            };
          };
        }

      default:
        return function (key) {
          return function (result, value) {
            if (value === undefined || options.skipNull && value === null || options.skipEmptyString && value === '') {
              return result;
            }

            if (value === null) {
              return [].concat(result, [encode(key, options)]);
            }

            return [].concat(result, [[encode(key, options), '=', encode(value, options)].join('')]);
          };
        };
    }
  }

  function parserForArrayFormat(options) {
    var result;

    switch (options.arrayFormat) {
      case 'index':
        return function (key, value, accumulator) {
          result = /\[(\d*)\]$/.exec(key);
          key = key.replace(/\[\d*\]$/, '');

          if (!result) {
            accumulator[key] = value;
            return;
          }

          if (accumulator[key] === undefined) {
            accumulator[key] = {};
          }

          accumulator[key][result[1]] = value;
        };

      case 'bracket':
        return function (key, value, accumulator) {
          result = /(\[\])$/.exec(key);
          key = key.replace(/\[\]$/, '');

          if (!result) {
            accumulator[key] = value;
            return;
          }

          if (accumulator[key] === undefined) {
            accumulator[key] = [value];
            return;
          }

          accumulator[key] = [].concat(accumulator[key], value);
        };

      case 'comma':
      case 'separator':
        return function (key, value, accumulator) {
          var isArray = typeof value === 'string' && value.includes(options.arrayFormatSeparator);
          var isEncodedArray = typeof value === 'string' && !isArray && decode(value, options).includes(options.arrayFormatSeparator);
          value = isEncodedArray ? decode(value, options) : value;
          var newValue = isArray || isEncodedArray ? value.split(options.arrayFormatSeparator).map(function (item) {
            return decode(item, options);
          }) : value === null ? value : decode(value, options);
          accumulator[key] = newValue;
        };

      case 'bracket-separator':
        return function (key, value, accumulator) {
          var isArray = /(\[\])$/.test(key);
          key = key.replace(/\[\]$/, '');

          if (!isArray) {
            accumulator[key] = value ? decode(value, options) : value;
            return;
          }

          var arrayValue = value === null ? [] : value.split(options.arrayFormatSeparator).map(function (item) {
            return decode(item, options);
          });

          if (accumulator[key] === undefined) {
            accumulator[key] = arrayValue;
            return;
          }

          accumulator[key] = [].concat(accumulator[key], arrayValue);
        };

      default:
        return function (key, value, accumulator) {
          if (accumulator[key] === undefined) {
            accumulator[key] = value;
            return;
          }

          accumulator[key] = [].concat(accumulator[key], value);
        };
    }
  }

  function validateArrayFormatSeparator(value) {
    if (typeof value !== 'string' || value.length !== 1) {
      throw new TypeError('arrayFormatSeparator must be single character string');
    }
  }

  function encode(value, options) {
    if (options.encode) {
      return options.strict ? strictUriEncode(value) : encodeURIComponent(value);
    }

    return value;
  }

  function decode(value, options) {
    if (options.decode) {
      return decodeUriComponent(value);
    }

    return value;
  }

  function keysSorter(input) {
    if (Array.isArray(input)) {
      return input.sort();
    }

    if (typeof input === 'object') {
      return keysSorter(Object.keys(input)).sort(function (a, b) {
        return Number(a) - Number(b);
      }).map(function (key) {
        return input[key];
      });
    }

    return input;
  }

  function removeHash(input) {
    var hashStart = input.indexOf('#');

    if (hashStart !== -1) {
      input = input.slice(0, hashStart);
    }

    return input;
  }

  function getHash(url) {
    var hash = '';
    var hashStart = url.indexOf('#');

    if (hashStart !== -1) {
      hash = url.slice(hashStart);
    }

    return hash;
  }

  function extract(input) {
    input = removeHash(input);
    var queryStart = input.indexOf('?');

    if (queryStart === -1) {
      return '';
    }

    return input.slice(queryStart + 1);
  }

  function parseValue(value, options) {
    if (options.parseNumbers && !Number.isNaN(Number(value)) && typeof value === 'string' && value.trim() !== '') {
      value = Number(value);
    } else if (options.parseBooleans && value !== null && (value.toLowerCase() === 'true' || value.toLowerCase() === 'false')) {
      value = value.toLowerCase() === 'true';
    }

    return value;
  }

  function parse(query, options) {
    options = Object.assign({
      decode: true,
      sort: true,
      arrayFormat: 'none',
      arrayFormatSeparator: ',',
      parseNumbers: false,
      parseBooleans: false
    }, options);
    validateArrayFormatSeparator(options.arrayFormatSeparator);
    var formatter = parserForArrayFormat(options); // Create an object with no prototype

    var ret = Object.create(null);

    if (typeof query !== 'string') {
      return ret;
    }

    query = query.trim().replace(/^[?#&]/, '');

    if (!query) {
      return ret;
    }

    for (var _iterator = _createForOfIteratorHelperLoose(query.split('&')), _step; !(_step = _iterator()).done;) {
      var param = _step.value;

      if (param === '') {
        continue;
      }

      var _splitOnFirst = splitOnFirst(options.decode ? param.replace(/\+/g, ' ') : param, '='),
          _key = _splitOnFirst[0],
          _value = _splitOnFirst[1]; // Missing `=` should be `null`:
      // http://w3.org/TR/2012/WD-url-20120524/#collect-url-parameters


      _value = _value === undefined ? null : ['comma', 'separator', 'bracket-separator'].includes(options.arrayFormat) ? _value : decode(_value, options);
      formatter(decode(_key, options), _value, ret);
    }

    for (var _i = 0, _Object$keys = Object.keys(ret); _i < _Object$keys.length; _i++) {
      var key = _Object$keys[_i];
      var value = ret[key];

      if (typeof value === 'object' && value !== null) {
        for (var _i2 = 0, _Object$keys2 = Object.keys(value); _i2 < _Object$keys2.length; _i2++) {
          var k = _Object$keys2[_i2];
          value[k] = parseValue(value[k], options);
        }
      } else {
        ret[key] = parseValue(value, options);
      }
    }

    if (options.sort === false) {
      return ret;
    }

    return (options.sort === true ? Object.keys(ret).sort() : Object.keys(ret).sort(options.sort)).reduce(function (result, key) {
      var value = ret[key];

      if (Boolean(value) && typeof value === 'object' && !Array.isArray(value)) {
        // Sort object keys, not values
        result[key] = keysSorter(value);
      } else {
        result[key] = value;
      }

      return result;
    }, Object.create(null));
  }

  exports.extract = extract;
  exports.parse = parse;

  exports.stringify = function (object, options) {
    if (!object) {
      return '';
    }

    options = Object.assign({
      encode: true,
      strict: true,
      arrayFormat: 'none',
      arrayFormatSeparator: ','
    }, options);
    validateArrayFormatSeparator(options.arrayFormatSeparator);

    var shouldFilter = function shouldFilter(key) {
      return options.skipNull && isNullOrUndefined(object[key]) || options.skipEmptyString && object[key] === '';
    };

    var formatter = encoderForArrayFormat(options);
    var objectCopy = {};

    for (var _i3 = 0, _Object$keys3 = Object.keys(object); _i3 < _Object$keys3.length; _i3++) {
      var key = _Object$keys3[_i3];

      if (!shouldFilter(key)) {
        objectCopy[key] = object[key];
      }
    }

    var keys = Object.keys(objectCopy);

    if (options.sort !== false) {
      keys.sort(options.sort);
    }

    return keys.map(function (key) {
      var value = object[key];

      if (value === undefined) {
        return '';
      }

      if (value === null) {
        return encode(key, options);
      }

      if (Array.isArray(value)) {
        if (value.length === 0 && options.arrayFormat === 'bracket-separator') {
          return encode(key, options) + '[]';
        }

        return value.reduce(formatter(key), []).join('&');
      }

      return encode(key, options) + '=' + encode(value, options);
    }).filter(function (x) {
      return x.length > 0;
    }).join('&');
  };

  exports.parseUrl = function (url, options) {
    options = Object.assign({
      decode: true
    }, options);

    var _splitOnFirst2 = splitOnFirst(url, '#'),
        url_ = _splitOnFirst2[0],
        hash = _splitOnFirst2[1];

    return Object.assign({
      url: url_.split('?')[0] || '',
      query: parse(extract(url), options)
    }, options && options.parseFragmentIdentifier && hash ? {
      fragmentIdentifier: decode(hash, options)
    } : {});
  };

  exports.stringifyUrl = function (object, options) {
    var _Object$assign;

    options = Object.assign((_Object$assign = {
      encode: true,
      strict: true
    }, _Object$assign[encodeFragmentIdentifier] = true, _Object$assign), options);
    var url = removeHash(object.url).split('?')[0] || '';
    var queryFromUrl = exports.extract(object.url);
    var parsedQueryFromUrl = exports.parse(queryFromUrl, {
      sort: false
    });
    var query = Object.assign(parsedQueryFromUrl, object.query);
    var queryString = exports.stringify(query, options);

    if (queryString) {
      queryString = "?" + queryString;
    }

    var hash = getHash(object.url);

    if (object.fragmentIdentifier) {
      hash = "#" + (options[encodeFragmentIdentifier] ? encode(object.fragmentIdentifier, options) : object.fragmentIdentifier);
    }

    return "" + url + queryString + hash;
  };

  exports.pick = function (input, filter, options) {
    var _Object$assign2;

    options = Object.assign((_Object$assign2 = {
      parseFragmentIdentifier: true
    }, _Object$assign2[encodeFragmentIdentifier] = false, _Object$assign2), options);

    var _exports$parseUrl = exports.parseUrl(input, options),
        url = _exports$parseUrl.url,
        query = _exports$parseUrl.query,
        fragmentIdentifier = _exports$parseUrl.fragmentIdentifier;

    return exports.stringifyUrl({
      url: url,
      query: filterObj(query, filter),
      fragmentIdentifier: fragmentIdentifier
    }, options);
  };

  exports.exclude = function (input, filter, options) {
    var exclusionFilter = Array.isArray(filter) ? function (key) {
      return !filter.includes(key);
    } : function (key, value) {
      return !filter(key, value);
    };
    return exports.pick(input, exclusionFilter, options);
  };
});
var queryString_1 = queryString.extract;
var queryString_2 = queryString.parse;
var queryString_3 = queryString.stringify;
var queryString_4 = queryString.parseUrl;

var __assign$1 = undefined && undefined.__assign || function () {
  __assign$1 = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign$1.apply(this, arguments);
};
/**
 * An example of a transformSearchString function that undoes encoding of
 * common JSON characters that are technically allowed in URLs.
 */

var JSON_SAFE_CHARS = /*#__PURE__*/"{}[],\":".split('').map(function (d) {
  return [d, encodeURIComponent(d)];
});
function transformSearchStringJsonSafe(searchString) {
  var str = searchString;

  for (var _i = 0, JSON_SAFE_CHARS_1 = JSON_SAFE_CHARS; _i < JSON_SAFE_CHARS_1.length; _i++) {
    var _a = JSON_SAFE_CHARS_1[_i],
        _char = _a[0],
        code = _a[1];
    str = str.replace(new RegExp('\\' + code, 'g'), _char);
  }

  return str;
}
/**
 * Update a location, wiping out parameters not included in encodedQuery
 * If a param is set to undefined it will be removed from the URL.
 */

function updateLocation(encodedQuery, location, stringifyOptions) {
  var encodedSearchString = queryString_3(encodedQuery, stringifyOptions);

  if (stringifyOptions && stringifyOptions.transformSearchString) {
    encodedSearchString = stringifyOptions.transformSearchString(encodedSearchString);
  }

  var search = encodedSearchString.length ? "?" + encodedSearchString : '';
  var href = queryString_4(location.href || '').url + search;

  var newLocation = __assign$1(__assign$1({}, location), {
    key: "" + Date.now(),
    // needed for some routers (e.g. react-router)
    href: href,
    search: search,
    query: encodedQuery
  });

  return newLocation;
}
/**
 * Update a location while retaining existing parameters.
 * If a param is set to undefined it will be removed from the URL.
 */

function updateInLocation(encodedQueryReplacements, location, stringifyOptions) {
  // explicitly avoid parsing numbers to ensure the
  // return type has the same shape as EncodeQuery
  var currQuery = queryString_2(location.search, {
    parseNumbers: false
  });

  var newQuery = __assign$1(__assign$1({}, currQuery), encodedQueryReplacements);

  return updateLocation(newQuery, location, stringifyOptions);
}

/**
 * Convert the values in query to strings via the encode functions configured
 * in paramConfigMap
 *
 * @param paramConfigMap Map from query name to { encode, decode } config
 * @param query Query updates mapping param name to decoded value
 */
function encodeQueryParams(paramConfigMap, query) {
  var encodedQuery = {};
  var paramNames = Object.keys(query);

  for (var _i = 0, paramNames_1 = paramNames; _i < paramNames_1.length; _i++) {
    var paramName = paramNames_1[_i];
    var decodedValue = query[paramName];

    if (!paramConfigMap[paramName]) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("Encoding parameter " + paramName + " as string since it was not configured.");
      } // NOTE: we could just not encode it, but it is probably convenient to have
      // it be included by default as a string type.


      encodedQuery[paramName] = decodedValue == null ? decodedValue : String(decodedValue);
    } else {
      encodedQuery[paramName] = paramConfigMap[paramName].encode(query[paramName]);
    }
  }

  return encodedQuery;
}

/**
 * Convert the values in query to strings via the encode functions configured
 * in paramConfigMap
 *
 * @param paramConfigMap Map from query name to { encode, decode } config
 * @param query Query updates mapping param name to decoded value
 */
function decodeQueryParams(paramConfigMap, encodedQuery) {
  var decodedQuery = {}; // iterate over all keys in the config (#30)

  var paramNames = Object.keys(paramConfigMap); // ensure any non configured keys that are in the URL are also included

  for (var _i = 0, _a = Object.keys(encodedQuery); _i < _a.length; _i++) {
    var encodedKey = _a[_i];

    if (paramConfigMap[encodedKey] == null) {
      paramNames.push(encodedKey);
    }
  }

  for (var _b = 0, paramNames_1 = paramNames; _b < paramNames_1.length; _b++) {
    var paramName = paramNames_1[_b];
    var encodedValue = encodedQuery[paramName];

    if (!paramConfigMap[paramName]) {
      if (process.env.NODE_ENV === 'development') {
        console.warn("Passing through parameter " + paramName + " during decoding since it was not configured.");
      } // NOTE: we could just not include it, but it is probably convenient to have
      // it default to be a string type.


      decodedQuery[paramName] = encodedValue;
    } else {
      decodedQuery[paramName] = paramConfigMap[paramName].decode(encodedValue);
    }
  }

  return decodedQuery;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license at
 * https://github.com/facebook/fbjs/blob/master/LICENSE
 */

/*eslint-disable no-self-compare */
var hasOwnProperty = Object.prototype.hasOwnProperty;
/**
 * inlined Object.is polyfill to avoid requiring consumers ship their own
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is
 */

function is(x, y) {
  // SameValue algorithm
  if (x === y) {
    // Steps 1-5, 7-10
    // Steps 6.b-6.e: +0 != -0
    // Added the nonzero y check to make Flow happy, but it is redundant
    return x !== 0 || y !== 0 || 1 / x === 1 / y;
  } else {
    // Step 6.a: NaN == NaN
    return x !== x && y !== y;
  }
}
/**
 * Performs equality by iterating through keys on an object and returning false
 * when any key has values which are not strictly equal between the arguments.
 * Returns true when the values of all keys are strictly equal.

 * @pbeshai modification of shallowEqual to take into consideration a map providing
 * equals functions
 */


function shallowEqual(objA, objB, equalMap) {
  var _a, _b;

  if (is(objA, objB)) {
    return true;
  }

  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }

  var keysA = Object.keys(objA);
  var keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  } // Test for A's keys different from B.


  for (var i = 0; i < keysA.length; i++) {
    var isEqual = (_b = (_a = equalMap === null || equalMap === void 0 ? void 0 : equalMap[keysA[i]]) === null || _a === void 0 ? void 0 : _a.equals) !== null && _b !== void 0 ? _b : is;

    if (!hasOwnProperty.call(objB, keysA[i]) || !isEqual(objA[keysA[i]], objB[keysA[i]])) {
      return false;
    }
  }

  return true;
}

function useUpdateRefIfShallowNew(ref, newValue, isEqual) {
  if (isEqual === void 0) {
    isEqual = shallowEqual;
  }

  var hasNew = (ref.current == null || newValue == null) && ref.current === newValue || !isEqual(ref.current, newValue);
  useEffect(function () {
    if (hasNew) {
      ref.current = newValue;
    }
  }, [ref, newValue, hasNew]);
}
function getSSRSafeSearchString(location) {
  // handle checking SSR (#13)
  if (typeof location === 'object') {
    // in browser
    if (typeof window !== 'undefined') {
      return location.search;
    } else {
      return queryString_1("" + location.pathname + (location.search ? location.search : ''));
    }
  }

  return '';
}

/**
 * Creates a new location object containing the specified query changes.
 * If replaceIn or pushIn are used as the updateType, then parameters
 * not specified in queryReplacements are retained. If replace or push
 * are used, only the values in queryReplacements will be available.
 * The default is pushIn.
 */

function createLocationWithChanges(queryReplacements, location, updateType, stringifyOptions) {
  if (updateType === void 0) {
    updateType = 'pushIn';
  }

  switch (updateType) {
    case 'replace':
    case 'push':
      return updateLocation(queryReplacements, location, stringifyOptions);

    case 'replaceIn':
    case 'pushIn':
    default:
      return updateInLocation(queryReplacements, location, stringifyOptions);
  }
}
/**
 * Updates the URL to the new location.
 */

function updateUrlQuery(history, location, updateType) {
  if (updateType === void 0) {
    updateType = 'pushIn';
  }

  switch (updateType) {
    case 'pushIn':
    case 'push':
      history.push(location);
      break;

    case 'replaceIn':
    case 'replace':
    default:
      history.replace(location);
      break;
  }
}

var providerlessContextValue = {
  location: {},
  getLocation: function getLocation() {
    return {};
  },
  setLocation: function setLocation() {}
};
var LocationContext = /*#__PURE__*/createContext(providerlessContextValue);
function useLocationContext() {
  var context = useContext(LocationContext);

  if (process.env.NODE_ENV !== 'production' && (context === undefined || context === providerlessContextValue)) {
    throw new Error('useQueryParams must be used within a QueryParamProvider');
  }

  return context;
}
/**
 * An internal-only context provider which provides down the most
 * recent location object and a callback to update the history.
 */

function LocationProvider(_a) {
  var history = _a.history,
      location = _a.location,
      children = _a.children,
      stringifyOptions = _a.stringifyOptions;
  var locationRef = useRef(location);
  useEffect(function () {
    locationRef.current = location;
  }, [location]); // TODO: we can probably simplify this now that we are reading location from history

  var getLocation = useCallback(function () {
    return locationRef.current;
  }, [locationRef]);
  var setLocation = useCallback(function (queryReplacements, updateType) {
    // A ref is needed here to stop setLocation updating constantly (see #46)
    locationRef.current = createLocationWithChanges(queryReplacements, history == null || history.location == null ? locationRef.current : history.location, updateType, stringifyOptions);

    if (history) {
      updateUrlQuery(history, locationRef.current, updateType);
    }
  }, [history, stringifyOptions]);
  return createElement(LocationContext.Provider, {
    value: {
      location: location,
      getLocation: getLocation,
      setLocation: setLocation
    }
  }, children);
}

var makeMemoizedQueryParser = function makeMemoizedQueryParser(initialSearchString) {
  var cachedSearchString = initialSearchString;
  var cachedParsedQuery = queryString_2(cachedSearchString || '');
  return function (newSearchString) {
    if (cachedSearchString !== newSearchString) {
      cachedSearchString = newSearchString;
      cachedParsedQuery = queryString_2(cachedSearchString);
    }

    return cachedParsedQuery;
  };
};
var sharedMemoizedQueryParser = /*#__PURE__*/makeMemoizedQueryParser();

/**
 * Helper to get the latest decoded value with smart caching.
 * Abstracted into its own function to allow re-use in a functional setter (#26)
 */

function getLatestDecodedValue(location, name, paramConfig, paramConfigRef, encodedValueCacheRef, decodedValueCacheRef) {
  var _a; // check if we have a new param config


  var hasNewParamConfig = !shallowEqual(paramConfigRef.current, paramConfig);
  var isValueEqual = (_a = paramConfig.equals) !== null && _a !== void 0 ? _a : shallowEqual; // read in the parsed query

  var parsedQuery = sharedMemoizedQueryParser(getSSRSafeSearchString(location) // get the latest location object
  ); // read in the encoded string value (we have to check cache if available because
  // sometimes the query string changes so we get a new parsedQuery but this value
  // didn't change, so we should avoid generating a new array or whatever value)

  var hasNewEncodedValue = !shallowEqual(encodedValueCacheRef.current, parsedQuery[name]);
  var encodedValue = hasNewEncodedValue ? parsedQuery[name] : encodedValueCacheRef.current; // only decode if we have changes to encoded value or the config.
  // check for undefined to handle initial case

  if (!hasNewEncodedValue && !hasNewParamConfig && decodedValueCacheRef.current !== undefined) {
    return decodedValueCacheRef.current;
  }

  var newDecodedValue = paramConfig.decode(encodedValue);
  var hasNewDecodedValue = (decodedValueCacheRef.current == null || newDecodedValue == null) && decodedValueCacheRef.current === newDecodedValue || !isValueEqual(decodedValueCacheRef.current, newDecodedValue); // if we have a new decoded value use it, otherwise use cached

  return hasNewDecodedValue ? newDecodedValue : decodedValueCacheRef.current;
}
/**
 * Given a query param name and query parameter configuration ({ encode, decode })
 * return the decoded value and a setter for updating it.
 *
 * The setter takes two arguments (newValue, updateType) where updateType
 * is one of 'replace' | 'replaceIn' | 'push' | 'pushIn', defaulting to
 * 'pushIn'.
 *
 * You may optionally pass in a rawQuery object, otherwise the query is derived
 * from the location available in the context.
 *
 * D = decoded type
 * D2 = return value from decode (typically same as D)
 */


var useQueryParam = function useQueryParam(name, paramConfig) {
  if (paramConfig === void 0) {
    paramConfig = StringParam;
  }

  var _a = useLocationContext(),
      location = _a.location,
      getLocation = _a.getLocation,
      setLocation = _a.setLocation; // read in the raw query


  var parsedQuery = sharedMemoizedQueryParser(getSSRSafeSearchString(location)); // make caches

  var encodedValueCacheRef = useRef();
  var paramConfigRef = useRef(paramConfig);
  var decodedValueCacheRef = useRef();
  var decodedValue = getLatestDecodedValue(location, name, paramConfig, paramConfigRef, encodedValueCacheRef, decodedValueCacheRef); // update cached values in a useEffect

  useUpdateRefIfShallowNew(encodedValueCacheRef, parsedQuery[name]);
  useUpdateRefIfShallowNew(paramConfigRef, paramConfig);
  useUpdateRefIfShallowNew(decodedValueCacheRef, decodedValue, paramConfig.equals); // create the setter, memoizing via useCallback

  var setValueDeps = {
    paramConfig: paramConfig,
    name: name,
    setLocation: setLocation,
    getLocation: getLocation
  };
  var setValueDepsRef = useRef(setValueDeps);
  setValueDepsRef.current = setValueDeps;
  var setValue = useCallback(function setValueCallback(newValue, updateType) {
    var _a;

    var deps = setValueDepsRef.current;
    var newEncodedValue; // allow functional updates #26

    if (typeof newValue === 'function') {
      // get latest decoded value to pass as a fresh arg to the setter fn
      var latestValue = getLatestDecodedValue(deps.getLocation(), deps.name, deps.paramConfig, paramConfigRef, encodedValueCacheRef, decodedValueCacheRef);
      decodedValueCacheRef.current = latestValue; // keep cache in sync

      newEncodedValue = deps.paramConfig.encode(newValue(latestValue));
    } else {
      newEncodedValue = deps.paramConfig.encode(newValue);
    } // update the URL


    deps.setLocation((_a = {}, _a[deps.name] = newEncodedValue, _a), updateType);
  }, []);
  return [decodedValue, setValue];
};

/**
 * Helper to get the latest decoded values with smart caching.
 * Abstracted into its own function to allow re-use in a functional setter (#26)
 */

function getLatestDecodedValues(location, paramConfigMap, paramConfigMapRef, parsedQueryRef, encodedValuesCacheRef, decodedValuesCacheRef) {
  // check if we have a new param config
  var hasNewParamConfigMap = !shallowEqual(paramConfigMapRef.current, paramConfigMap); // read in the parsed query

  var parsedQuery = sharedMemoizedQueryParser(getSSRSafeSearchString(location) // get the latest location object
  ); // check if new encoded values are around (new parsed query).
  // can use triple equals since we already cache this value

  var hasNewParsedQuery = parsedQueryRef.current !== parsedQuery; // if nothing has changed, use existing.. so long as we have existing.

  if (!hasNewParsedQuery && !hasNewParamConfigMap && encodedValuesCacheRef.current !== undefined) {
    return {
      encodedValues: encodedValuesCacheRef.current,
      decodedValues: decodedValuesCacheRef.current
    };
  }

  var encodedValuesCache = encodedValuesCacheRef.current || {};
  var decodedValuesCache = decodedValuesCacheRef.current || {};
  var encodedValues = {}; // we have new encoded values, so let's get new decoded values.
  // recompute new values but only for those that changed

  var paramNames = Object.keys(paramConfigMap);
  var decodedValues = {};

  for (var _i = 0, paramNames_1 = paramNames; _i < paramNames_1.length; _i++) {
    var paramName = paramNames_1[_i]; // do we have a new encoded value?

    var paramConfig = paramConfigMap[paramName];
    var hasNewEncodedValue = !shallowEqual(encodedValuesCache[paramName], parsedQuery[paramName]); // if we have a new encoded value, re-decode. otherwise reuse cache

    var encodedValue = void 0;
    var decodedValue = void 0;

    if (hasNewEncodedValue || encodedValuesCache[paramName] === undefined && decodedValuesCache[paramName] === undefined) {
      encodedValue = parsedQuery[paramName];
      decodedValue = paramConfig.decode(encodedValue);
    } else {
      encodedValue = encodedValuesCache[paramName];
      decodedValue = decodedValuesCache[paramName];
    }

    encodedValues[paramName] = encodedValue;
    decodedValues[paramName] = decodedValue;
  } // keep referential equality for decoded valus if we didn't actually change anything


  var hasNewDecodedValues = !shallowEqual(decodedValuesCacheRef.current, decodedValues, paramConfigMap);
  return {
    encodedValues: encodedValues,
    decodedValues: hasNewDecodedValues ? decodedValues : decodedValuesCacheRef.current
  };
}
/**
 * Given a query parameter configuration (mapping query param name to { encode, decode }),
 * return an object with the decoded values and a setter for updating them.
 */


var useQueryParams = function useQueryParams(paramConfigMap) {
  var _a = useLocationContext(),
      location = _a.location,
      getLocation = _a.getLocation,
      setLocation = _a.setLocation; // read in the raw query


  var parsedQuery = sharedMemoizedQueryParser(getSSRSafeSearchString(location)); // make caches

  var paramConfigMapRef = useRef(paramConfigMap);
  var parsedQueryRef = useRef(parsedQuery);
  var encodedValuesCacheRef = useRef(undefined); // undefined for initial check

  var decodedValuesCacheRef = useRef({}); // memoize paramConfigMap to make the API nicer for consumers.
  // otherwise we'd have to useQueryParams(useMemo(() => { foo: NumberParam }, []))

  paramConfigMap = shallowEqual(paramConfigMap, paramConfigMapRef.current) ? paramConfigMapRef.current : paramConfigMap; // decode all the values if we have changes

  var _b = getLatestDecodedValues(location, paramConfigMap, paramConfigMapRef, parsedQueryRef, encodedValuesCacheRef, decodedValuesCacheRef),
      encodedValues = _b.encodedValues,
      decodedValues = _b.decodedValues; // update cached values in useEffects


  useUpdateRefIfShallowNew(parsedQueryRef, parsedQuery);
  useUpdateRefIfShallowNew(paramConfigMapRef, paramConfigMap);
  useUpdateRefIfShallowNew(encodedValuesCacheRef, encodedValues);
  useUpdateRefIfShallowNew(decodedValuesCacheRef, decodedValues, function (a, b) {
    return shallowEqual(a, b, paramConfigMap);
  }); // create a setter for updating multiple query params at once

  var setQueryDeps = {
    paramConfigMap: paramConfigMap,
    setLocation: setLocation,
    getLocation: getLocation
  };
  var setQueryDepsRef = useRef(setQueryDeps);
  setQueryDepsRef.current = setQueryDeps;
  var setQuery = useCallback(function (changes, updateType) {
    var deps = setQueryDepsRef.current;
    var encodedChanges;

    if (typeof changes === 'function') {
      // get latest decoded value to pass as a fresh arg to the setter fn
      var latestValues = getLatestDecodedValues(deps.getLocation(), deps.paramConfigMap, paramConfigMapRef, parsedQueryRef, encodedValuesCacheRef, decodedValuesCacheRef).decodedValues;
      decodedValuesCacheRef.current = latestValues; // keep cache in sync

      encodedChanges = encodeQueryParams(deps.paramConfigMap, changes(latestValues));
    } else {
      // encode as strings for the URL
      encodedChanges = encodeQueryParams(deps.paramConfigMap, changes);
    } // update the URL


    deps.setLocation(encodedChanges, updateType);
  }, []); // no longer Partial

  return [decodedValues, setQuery];
};

var __assign$2 = undefined && undefined.__assign || function () {
  __assign$2 = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign$2.apply(this, arguments);
};
/**
 * HOC to provide query parameters via props `query` and `setQuery`
 * NOTE: I couldn't get type to automatically infer generic when
 * using the format withQueryParams(config)(component), so I switched
 * to withQueryParams(config, component).
 * See: https://github.com/microsoft/TypeScript/issues/30134
 */

function withQueryParams(paramConfigMap, WrappedComponent) {
  // return a FC that takes props excluding query and setQuery
  var Component = function Component(props) {
    var _a = useQueryParams(paramConfigMap),
        query = _a[0],
        setQuery = _a[1]; // see https://github.com/microsoft/TypeScript/issues/28938#issuecomment-450636046 for why `...props as P`


    return createElement(WrappedComponent, __assign$2({
      query: query,
      setQuery: setQuery
    }, props));
  };

  Component.displayName = "withQueryParams(" + (WrappedComponent.displayName || WrappedComponent.name || 'Component') + ")";
  return Component;
}
/**
 * HOC to provide query parameters via props mapToProps (similar to
 * react-redux connect style mapStateToProps)
 * NOTE: I couldn't get type to automatically infer generic when
 * using the format withQueryParams(config)(component), so I switched
 * to withQueryParams(config, component).
 * See: https://github.com/microsoft/TypeScript/issues/30134
 */

function withQueryParamsMapped(paramConfigMap, mapToProps, WrappedComponent) {
  // return a FC that takes props excluding query and setQuery
  var Component = function Component(props) {
    var _a = useQueryParams(paramConfigMap),
        query = _a[0],
        setQuery = _a[1];

    var propsToAdd = mapToProps(query, setQuery, props); // see https://github.com/microsoft/TypeScript/issues/28938#issuecomment-450636046 for why `...props as P`

    return createElement(WrappedComponent, __assign$2({}, propsToAdd, props));
  };

  Component.displayName = "withQueryParams(" + (WrappedComponent.displayName || WrappedComponent.name || 'Component') + ")";
  return Component;
}

var QueryParams = function QueryParams(_a) {
  var config = _a.config,
      children = _a.children;

  var _b = useQueryParams(config),
      query = _b[0],
      setQuery = _b[1];

  return children({
    query: query,
    setQuery: setQuery
  });
};

var __assign$3 = undefined && undefined.__assign || function () {
  __assign$3 = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign$3.apply(this, arguments);
};

var cachedWindowHistory;
var cachedAdaptedWindowHistory;
/**
 * Adapts standard DOM window history to work with our
 * { replace, push } interface.
 *
 * @param history Standard history provided by DOM
 */

function adaptWindowHistory(history) {
  if (history === cachedWindowHistory && cachedAdaptedWindowHistory != null) {
    return cachedAdaptedWindowHistory;
  }

  var adaptedWindowHistory = {
    replace: function replace(location) {
      history.replaceState(location.state, '', location.protocol + "//" + location.host + location.pathname + location.search);
    },
    push: function push(location) {
      history.pushState(location.state, '', location.protocol + "//" + location.host + location.pathname + location.search);
    },

    get location() {
      return window.location;
    }

  };
  cachedWindowHistory = history;
  cachedAdaptedWindowHistory = adaptedWindowHistory;
  return adaptedWindowHistory;
} // we use a lazy caching solution to prevent #46 from happening


var cachedReachHistory;
var cachedAdaptedReachHistory;
/**
 * Adapts @reach/router history to work with our
 * { replace, push } interface.
 *
 * @param history globalHistory from @reach/router
 */

function adaptReachHistory(history) {
  if (history === cachedReachHistory && cachedAdaptedReachHistory != null) {
    return cachedAdaptedReachHistory;
  }

  var adaptedReachHistory = {
    replace: function replace(location) {
      history.navigate(location.protocol + "//" + location.host + location.pathname + location.search, {
        replace: true
      });
    },
    push: function push(location) {
      history.navigate(location.protocol + "//" + location.host + location.pathname + location.search, {
        replace: false
      });
    },

    get location() {
      return history.location;
    }

  };
  cachedReachHistory = history;
  cachedAdaptedReachHistory = adaptedReachHistory;
  return adaptedReachHistory;
}
/**
 * Helper to produce the context value falling back to
 * window history and location if not provided.
 */


function getLocationProps(_a) {
  var _b = _a === void 0 ? {} : _a,
      history = _b.history,
      location = _b.location;

  var hasWindow = typeof window !== 'undefined';

  if (hasWindow) {
    if (!history) {
      history = adaptWindowHistory(window.history);
    }

    if (!location) {
      location = window.location;
    }
  }

  if (!location) {
    throw new Error("\n        Could not read the location. Is the router wired up correctly?\n      ");
  }

  return {
    history: history,
    location: location
  };
}
/**
 * Context provider for query params to have access to the
 * active routing system, enabling updates to the URL.
 */

function QueryParamProvider(_a) {
  var children = _a.children,
      ReactRouterRoute = _a.ReactRouterRoute,
      reachHistory = _a.reachHistory,
      history = _a.history,
      location = _a.location,
      stringifyOptions = _a.stringifyOptions; // cache the stringify options object so we users can just do
  // <QueryParamProvider stringifyOptions={{ encode: false }} />

  var stringifyOptionsRef = useRef(stringifyOptions);
  var hasNewStringifyOptions = !shallowEqual(stringifyOptionsRef.current, stringifyOptions);
  var stringifyOptionsCached = hasNewStringifyOptions ? stringifyOptions : stringifyOptionsRef.current;
  useEffect(function () {
    stringifyOptionsRef.current = stringifyOptionsCached;
  }, [stringifyOptionsCached]); // if we have React Router, use it to get the context value

  if (ReactRouterRoute) {
    return createElement(ReactRouterRoute, null, function (routeProps) {
      return createElement(LocationProvider, __assign$3({
        stringifyOptions: stringifyOptionsCached
      }, getLocationProps(routeProps)), children);
    });
  } // if we are using reach router, use its history


  if (reachHistory) {
    return createElement(LocationProvider, __assign$3({
      stringifyOptions: stringifyOptionsCached
    }, getLocationProps({
      history: adaptReachHistory(reachHistory),
      location: location
    })), children);
  } // neither reach nor react-router, so allow manual overrides


  return createElement(LocationProvider, __assign$3({
    stringifyOptions: stringifyOptionsCached
  }, getLocationProps({
    history: history,
    location: location
  })), children);
}

var _excluded = ["children", "shallow"];
var pathnameRegex = /(?:(?![#\?])[\s\S])+/;

function NextQueryParamProvider(_ref) {
  var children = _ref.children,
      _ref$shallow = _ref.shallow,
      shallow = _ref$shallow === void 0 ? true : _ref$shallow,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);

  var router = useRouter();
  var match = router.asPath.match(pathnameRegex);
  var pathname = match ? match[0] : router.asPath;
  var location = useMemo(function () {
    if (typeof window !== 'undefined') {
      // For SSG, no query parameters are available on the server side,
      // since they can't be known at build time. Therefore to avoid
      // markup mismatches, we need a two-part render in this case that
      // patches the client with the updated query parameters lazily.
      // Note that for SSR `router.isReady` will be `true` immediately
      // and therefore there's no two-part render in this case.
      if (router.isReady) {
        return window.location;
      } else {
        return {
          search: ''
        };
      }
    } else {
      // On the server side we only need a subset of the available
      // properties of `Location`. The other ones are only necessary
      // for interactive features on the client.
      return {
        search: router.asPath.replace(pathnameRegex, '')
      };
    }
  }, [router.asPath, router.isReady]);
  var history = useMemo(function () {
    function createUpdater(routeFn) {
      return function updater(_ref2) {
        var hash = _ref2.hash,
            search = _ref2.search;
        routeFn({
          pathname: router.pathname,
          search: search,
          hash: hash
        }, {
          pathname: pathname,
          search: search,
          hash: hash
        }, {
          shallow: shallow,
          scroll: false
        });
      };
    }

    return {
      push: createUpdater(router.push),
      replace: createUpdater(router.replace),
      location: location
    };
  }, [location, pathname, router, shallow]);
  return React__default.createElement(QueryParamProvider, Object.assign({}, rest, {
    history: history,
    location: location
  }), children);
}

var NextQueryParamProvider$1 = /*#__PURE__*/memo(NextQueryParamProvider);

export { ArrayParam, BooleanParam, DateParam, DateTimeParam, DelimitedArrayParam, DelimitedNumericArrayParam, JsonParam, NextQueryParamProvider$1 as NextQueryParamProvider, NumberParam, NumericArrayParam, NumericObjectParam, ObjectParam, QueryParamProvider, QueryParams, StringParam, createEnumParam, decodeArray, decodeBoolean, decodeDate, decodeDelimitedArray, decodeDelimitedNumericArray, decodeEnum, decodeJson, decodeNumber, decodeNumericArray, decodeNumericObject, decodeObject, decodeQueryParams, decodeString, encodeArray, encodeBoolean, encodeDate, encodeDelimitedArray, encodeDelimitedNumericArray, encodeJson, encodeNumber, encodeNumericArray, encodeNumericObject, encodeObject, encodeQueryParams, encodeString, transformSearchStringJsonSafe, updateInLocation, updateLocation, useQueryParam, useQueryParams, withDefault, withQueryParams, withQueryParamsMapped };
//# sourceMappingURL=next-query-params.esm.js.map
